import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './TeamUpAd.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faBed,
  faBath,
  faCoins,
  faCalendarAlt,
  faUsers,
  faPhone,
  faCheckCircle,
  faTimesCircle,
  faHome,
  faSearch,
  faHouse,
  faHomeAlt,
  faHomeUser,
  faImages,
  faFlag,
  faHeart as faHeartSolid,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';

import { cities, regions } from '../constants/locations';
import { lifestyleOptions } from '../constants/lifestyle';
import { genderOptions } from '../constants/gender';
import { bedroomsOptions, bathroomsOptions } from '../constants/flatRooms';
import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc, setDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { getTimeAgo } from '../constants/timeUtils';
import { getOptimizedImageUrl } from '../cloudinaryService';

// ─── Small lookup / formatting helpers ─────────────────────────────────────
// These mirror the same {value, ar, en} shape used across TeamUpFormControls
// and TeamUpStepContent, so an ad's stored `value`s can be turned back into
// a localized label for display.

function findLabel(list, value, getLocalizedText) {
  if (value === undefined || value === null || value === '') return '';
  const match = (list || []).find((item) => String(item.value) === String(value));
  return match ? getLocalizedText(match.ar, match.en) : String(value);
}

function formatDate(iso, getLocalizedText) {
  if (!iso) return '';
  const locale = getLocalizedText('ar-EG', 'en-US');
  return new Date(`${iso}T00:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatMoney(value, getLocalizedText) {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return `${num.toLocaleString(getLocalizedText('ar-EG', 'en-US'))} ${getLocalizedText('جنيه', 'EGP')}`;
}

// Egyptian numbers are stored as 11-digit local numbers (e.g. 01012345678).
// wa.me needs the international form without the leading 0 (2010...).
function toWhatsappHref(localNumber) {
  if (!localNumber) return null;
  const digits = localNumber.replace(/^0/, '');
  return `https://wa.me/20${digits}`;
}

// Converts western digits to Arabic-Indic digits when displaying Arabic text.
function toLocalizedDigits(value, currentLanguage) {
  if (value === null || value === undefined) return value;
  if (currentLanguage !== 'ar') return String(value);
  const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return String(value).replace(/[0-9]/g, (d) => arabicDigits[d]);
}

function TeamUpAd({ currentLanguage, getLocalizedText, onOpenLogin }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [showFlatPhotos, setShowFlatPhotos] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [loadingFavorite, setLoadingFavorite] = useState(true);

  useEffect(() => {
    const fetchTeamUpAd = async () => {
      setLoading(true);
      setError(null);
      try {
        const docRef = doc(db, 'team_up_ads', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setAd({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError('not_found');
        }
      } catch (err) {
        console.error('Error fetching team up ad:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamUpAd();
  }, [id]);

  useEffect(() => {
    document.documentElement.setAttribute('dir', currentLanguage === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // Check if this team-up ad is in the current user's favourites
  useEffect(() => {
    const checkIfFavorited = async () => {
      if (!ad) return;

      if (!auth.currentUser) {
        setIsFavorited(false);
        setLoadingFavorite(false);
        return;
      }

      try {
        const userRef = doc(db, 'verified_users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const favourites = userSnap.data().favourite_ads_id || [];
          setIsFavorited(favourites.includes(ad.id));
        }
      } catch (err) {
        console.error('Error checking favourite:', err);
      } finally {
        setLoadingFavorite(false);
      }
    };

    checkIfFavorited();
  }, [ad]);

  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!auth.currentUser) {
      if (typeof onOpenLogin === 'function') {
        onOpenLogin();
      } else {
        alert(getLocalizedText('يجب تسجيل الدخول لإضافة إلى المفضلة', 'Please login to add to favourites'));
      }
      return;
    }

    if (!ad) return;

    setLoadingFavorite(true);
    try {
      const userRef = doc(db, 'verified_users', auth.currentUser.uid);
      if (isFavorited) {
        await updateDoc(userRef, { favourite_ads_id: arrayRemove(ad.id) });
        setIsFavorited(false);
      } else {
        await setDoc(userRef, { favourite_ads_id: arrayUnion(ad.id) }, { merge: true });
        setIsFavorited(true);
      }
    } catch (err) {
      console.error('Error toggling favourite:', err);
      alert(getLocalizedText('حدث خطأ، حاول مرة أخرى', 'Error, please try again'));
    } finally {
      setLoadingFavorite(false);
    }
  };

  const handleClose = () => {
    navigate('/');
  };

  // Contact clicks aren't login-gated today (tel:/wa.me links are built directly
  // below) — this is just a hook for future tracking/analytics.
  const handleContact = (channel) => {
    // no-op for now
  };

  // Opens the report form pre-filled with this ad's link, same flow used on
  // the regular Ad page (Ad.js).
  const REPORT_FORM_BASE_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSchiMdQiaVqRVlKG5uPVH_-9565dgbqHMHOPhseiIvr--ypGQ/viewform';
  const REPORT_ENTRY_ID = 'entry.723940996'; // "The Link for the Ad you want to report"

  const handleReportButtonClick = () => {
    const adLink = typeof window !== 'undefined' ? window.location.href : '';
    const prefillUrl = `${REPORT_FORM_BASE_URL}?usp=pp_url&${REPORT_ENTRY_ID}=${encodeURIComponent(adLink)}`;
    window.open(prefillUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="tua-page" dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}>
        <div className="tua-container">
          <span className="tua-close-button" onClick={handleClose}>×</span>
          <p style={{ textAlign: 'center', padding: '40px' }}>
            {getLocalizedText('جاري التحميل...', 'Loading...')}
          </p>
        </div>
      </div>
    );
  }

  if (error || !ad) {
    return (
      <div className="tua-page" dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}>
        <div className="tua-container">
          <span className="tua-close-button" onClick={handleClose}>×</span>
          <p style={{ textAlign: 'center', padding: '40px' }}>
            {getLocalizedText('لم يتم العثور على هذا الطلب.', 'This request was not found.')}
          </p>
        </div>
      </div>
    );
  }

  const flatChosen = ad.has_flat === 'yes';

  const genderOption = genderOptions.find((g) => g.value === ad.gender);
  const genderLabel = findLabel(genderOptions, ad.gender, getLocalizedText);

  const cityValue = flatChosen ? ad.city : ad.preferred_city;
  const cityLabel = cityValue ? findLabel(cities, cityValue, getLocalizedText) : '';
  const regionList = regions[cityValue] || [];

  const regionLabel = flatChosen
    ? findLabel(regionList, ad.region, getLocalizedText)
    : (Array.isArray(ad.preferred_regions) && ad.preferred_regions.length > 0
        ? ad.preferred_regions
            .map((value) => findLabel(regionList, value, getLocalizedText))
            .join(getLocalizedText('، ', ', '))
        : '');

  const phoneHref = ad.contact_phone ? `tel:${ad.contact_phone}` : null;
  const whatsappHref = toWhatsappHref(ad.contact_whatsapp);
  const timeAgo = getTimeAgo(ad.createdAt, currentLanguage);

  return (
    <div className="tua-page" dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}>
      <div className="tua-container">
        <span className="tua-close-button" onClick={handleClose}>×</span>

        {/* ── Header ────────────────────────────────────────────── */}
        <div className="tua-header">
        </div>

        <div className="tua-content">
          <div className="tua-info-box">
          <div className="tua-avatar-wrap">
            {ad.profile_photo ? (
              <img src={getOptimizedImageUrl(ad.profile_photo, 'avatar')} alt={ad.personal_name} className="tua-avatar" />
            ) : (
              <div className="tua-avatar tua-avatar-placeholder">
                {ad.personal_name ? ad.personal_name.charAt(0) : '?'}
              </div>
            )}
          </div>

          <div className="tua-header-info">
            <h2 className="tua-name">{ad.personal_name}</h2>
            <div className="tua-subline">
              
              {genderLabel && <span>{genderLabel}</span>}
              {ad.age ? (
                <>
                  <span className="tua-dot">•</span>
                  <span>{getLocalizedText(`${toLocalizedDigits(ad.age, currentLanguage)} سنة`, `${ad.age} yrs`)}</span>
                </>
              ) : null}
            </div>
            
            {ad.bio && (
              <div className="tua-bio-wrap">
                <p className={`tua-bio ${isBioExpanded ? 'is-expanded' : ''}`}>{ad.bio}</p>
                {ad.bio.length > 120 && (
                  <span className="tua-bio-toggle" onClick={() => setIsBioExpanded((prev) => !prev)}>
                    {isBioExpanded
                      ? getLocalizedText('عرض أقل', 'Show less')
                      : getLocalizedText('عرض المزيد', 'Show more')}
                  </span>
                )}
              </div>
            )}
          </div>
          </div>

          <div className="tua-fav-row">
            <button
              type="button"
              className={`tua-favorite-btn ${isFavorited ? 'favorited' : ''}`}
              onClick={toggleFavorite}
              disabled={loadingFavorite}
              aria-label={getLocalizedText('المفضلة', 'Favourite')}
              title={getLocalizedText('المفضلة', 'Favourite')}
            >
              <FontAwesomeIcon icon={isFavorited ? faHeartSolid : faHeartRegular} />
            </button>
          </div>

          {/* ── Team size ───────────────────────────────────────── */}
          <div className="tua-section">
            <div className="tua-section-title">
              {getLocalizedText('حجم الفريق', 'Team Size')}
            </div>
            <div className="tua-team-fraction">
              <FontAwesomeIcon icon={faUsers} className="tua-detail-icon" />
              {getLocalizedText(
                `الفريق الحالي ${toLocalizedDigits(ad.current_team_size, currentLanguage)} من أصل ${toLocalizedDigits(ad.desired_team_size, currentLanguage)} أعضاء.`,
                `Currently ${ad.current_team_size} out of ${ad.desired_team_size} members.`
              )}
            </div>
          </div>

          {/* ── Flat details (has_flat) or Looking-for (no flat) ── */}
          {flatChosen ? (
            <div className="tua-section">
              <div className="tua-section-title">
                {getLocalizedText('عثرتُ على شقة يمكننا استئجارها و مشاركتها', 'I found a flat that we can rent and share')}
              </div>
              <div className="tua-detail-grid">
                <div className="tua-detail-item">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="tua-detail-icon" />
                  <span>{[cityLabel, regionLabel].filter(Boolean).join(' — ')}</span>
                </div>
                <div className="tua-detail-item">
                  <FontAwesomeIcon icon={faBed} className="tua-detail-icon" />
                  <span>
                    {getLocalizedText(
                      `${findLabel(bedroomsOptions, ad.bedrooms, getLocalizedText)} غرف نوم`,
                      `${findLabel(bedroomsOptions, ad.bedrooms, getLocalizedText)} bedrooms`
                    )}
                  </span>
                </div>
                <div className="tua-detail-item">
                  <FontAwesomeIcon icon={faBath} className="tua-detail-icon" />
                  <span>
                    {getLocalizedText(
                      `${findLabel(bathroomsOptions, ad.bathrooms, getLocalizedText)} حمامات`,
                      `${findLabel(bathroomsOptions, ad.bathrooms, getLocalizedText)} bathrooms`
                    )}
                  </span>
                </div>
                <div className="tua-detail-item">
                  <FontAwesomeIcon icon={faCoins} className="tua-detail-icon" />
                  <span>
                    {getLocalizedText('إيجار شقة', 'Flat Rent')}:{' '}
                    {formatMoney(ad.monthly_rent, getLocalizedText)} / {getLocalizedText('شهرياً', 'month')}
                  </span>
                </div>
                {ad.refundable_insurance ? (
                  <div className="tua-detail-item">
                    <FontAwesomeIcon icon={faCoins} className="tua-detail-icon" />
                    <span>
                      {getLocalizedText('تأمين قابل للاسترداد', 'Refundable Insurance')}:{' '}
                      {formatMoney(ad.refundable_insurance, getLocalizedText)}
                    </span>
                  </div>
                ) : null}
                <div className="tua-detail-item">
                  <FontAwesomeIcon icon={faCalendarAlt} className="tua-detail-icon" />
                  <span>{formatDate(ad.move_in_date, getLocalizedText)}</span>
                </div>
              </div>
               
              {ad.open_to_other_flat && (
                <div className={`tua-open-note ${ad.open_to_other_flat === 'yes' ? 'is-positive' : 'is-negative'}`}>
                  <FontAwesomeIcon icon={ad.open_to_other_flat === 'yes' ? faCheckCircle : faTimesCircle} />
                  {ad.open_to_other_flat === 'yes'
                    ? getLocalizedText('منفتح للبحث عن شقة أخرى', 'Open to search for another flat')
                    : getLocalizedText('غير منفتح للبحث عن شقة أخرى', 'Not open to another flat')}
                </div>
              )}

              {ad.flat_description && (
                <>
                  <div className="tua-subsection-title">
                    {getLocalizedText('وصف الشقة التي وجدتها', 'About  flat I found')}
                  </div>
                  <p className="tua-description">{ad.flat_description}</p>
                </>
              )}

              {Array.isArray(ad.flat_photos) && ad.flat_photos.length > 0 && (
                <>
                  <div className="tua-subsection-title">
                    {getLocalizedText('صور الشقة', 'Flat Photos')}
                  </div>
                  {!showFlatPhotos ? (
                    <button
                      type="button"
                      className="tua-show-photos-btn"
                      onClick={() => setShowFlatPhotos(true)}
                    >
                      <FontAwesomeIcon icon={faImages} />
                      {getLocalizedText(
                        `عرض الصور (${toLocalizedDigits(ad.flat_photos.length, currentLanguage)})`,
                        `Show Photos (${ad.flat_photos.length})`
                      )}
                    </button>
                  ) : (
                    <div className="tua-flat-photos-grid">
                      {ad.flat_photos.map((src, index) => (
                        <a
                          key={index}
                          href={src}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tua-flat-photo-thumb"
                        >
                          <img src={getOptimizedImageUrl(src, 'card')} alt={`flat-${index}`} loading="lazy" />
                        </a>
                      ))}
                    </div>
                  )}
                </>
              )}

              </div>
          ) : (
            <div className="tua-section">
              <div className="tua-section-title">
                <FontAwesomeIcon icon={faSearch} className="tua-section-icon" />
                {getLocalizedText('سنبحث عن شقة يمكننا استئجارها و مشاركتها', 'We will look for a flat we can rent and share')}
              </div>
              <div className="tua-detail-grid">
                <div className="tua-detail-item">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="tua-detail-icon" />
                  <span>{[cityLabel, regionLabel].filter(Boolean).join(' — ')}</span>
                </div>
                <div className="tua-detail-item">
                  <FontAwesomeIcon icon={faCoins} className="tua-detail-icon" />
                  <span>
                    {formatMoney(ad.budget_per_person, getLocalizedText)} / {getLocalizedText('للفرد', 'per person')}
                  </span>
                </div>
                <div className="tua-detail-item">
                  <FontAwesomeIcon icon={faCalendarAlt} className="tua-detail-icon" />
                  <span>{formatDate(ad.preferred_move_in_date, getLocalizedText)}</span>
                </div>
              </div>

              {ad.looking_for_description && (
                <>
                  <div className="tua-subsection-title">
                    {getLocalizedText('ماذا نبحث عنه', "What We're Looking For")}
                  </div>
                  <p className="tua-description">{ad.looking_for_description}</p>
                </>
              )}
            </div>
          )}

          {/* ── Lifestyle ───────────────────────────────────────── */}
          {Array.isArray(ad.lifestyle) && ad.lifestyle.length > 0 && (
            <div className="tua-section">
              <div className="tua-section-title">{getLocalizedText('نمط الحياة وقواعد المنزل', 'Lifestyle & House Rules')}</div>
              <div className="tua-lifestyle-chips">
                {ad.lifestyle.map((value) => {
                  const opt = lifestyleOptions.find((o) => o.value === value);
                  if (!opt) return null;
                  return (
                    <span className="tua-chip" key={value}>
                      <div className="tua-lifestyle-icon-wrap">
                      <img src={opt.icon} alt={opt.en} className="tua-chip-icon" />
                      </div>
                      {getLocalizedText(opt.ar, opt.en)}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

         {timeAgo && <div className="tua-time-ago">{timeAgo}</div>}

      </div>

        {/* ── Contact bar ─────────────────────────────────────────── */}
        <div className="tua-contact-bar">
          {phoneHref && (
            <a
              href={phoneHref}
              className="tua-btn tua-btn-ghost"
              onClick={() => handleContact('phone')}
            >
              <FontAwesomeIcon icon={faPhone} />
              {getLocalizedText('اتصال', 'Call')}
            </a>
          )}
          {whatsappHref && (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="tua-btn tua-btn-primary"
              onClick={() => handleContact('whatsapp')}
            >
              <FontAwesomeIcon icon={faWhatsapp} />
              {getLocalizedText('واتساب', 'WhatsApp')}
            </a>
          )}
          <button
            type="button"
            className="tua-btn tua-btn-report"
            onClick={handleReportButtonClick}
            title={getLocalizedText('إبلاغ عن الإعلان', 'Report this ad')}
            aria-label={getLocalizedText('إبلاغ عن الإعلان', 'Report this ad')}
          >
            <FontAwesomeIcon icon={faFlag} />
            {getLocalizedText('إبلاغ', 'Report')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TeamUpAd;